import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook PWA complet pour Cabinet 4.0
 * Gère l'installation, les mises à jour, le mode offline et les notifications
 */

// ============================================
// CONSTANTES
// ============================================

const SW_PATH = '/service-worker.js';
const SW_CHECK_INTERVAL = 60 * 60 * 1000; // Vérifier les mises à jour toutes les heures

// ============================================
// HOOK PRINCIPAL
// ============================================

export function usePWA() {
  // États
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isStandalone, setIsStandalone] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [swVersion, setSwVersion] = useState(null);
  
  // Références
  const deferredPrompt = useRef(null);
  const updateIntervalRef = useRef(null);

  // ============================================
  // DÉTECTION MODE PWA
  // ============================================

  useEffect(() => {
    // Détecter si l'app est en mode standalone (PWA installée)
    const checkStandalone = () => {
      const standalone = 
        window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: fullscreen)').matches ||
        window.navigator.standalone === true ||
        document.referrer.includes('android-app://');
      
      setIsStandalone(standalone);
      setIsInstalled(standalone);
    };

    checkStandalone();

    // Écouter les changements de mode d'affichage
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = (e) => {
      setIsStandalone(e.matches);
      setIsInstalled(e.matches);
    };
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // ============================================
  // GESTION ONLINE/OFFLINE
  // ============================================

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      console.log('[PWA] Connexion rétablie');
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('[PWA] Mode hors ligne');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // ============================================
  // INSTALLATION PWA
  // ============================================

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setIsInstallable(true);
      console.log('[PWA] Installation disponible');
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      deferredPrompt.current = null;
      console.log('[PWA] Application installée');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  /**
   * Déclencher l'installation de la PWA
   */
  const installPWA = useCallback(async () => {
    if (!deferredPrompt.current) {
      console.warn('[PWA] Prompt d\'installation non disponible');
      return { success: false, reason: 'not_available' };
    }

    try {
      deferredPrompt.current.prompt();
      const { outcome } = await deferredPrompt.current.userChoice;
      
      deferredPrompt.current = null;
      setIsInstallable(false);

      if (outcome === 'accepted') {
        console.log('[PWA] Installation acceptée');
        return { success: true };
      } else {
        console.log('[PWA] Installation refusée');
        return { success: false, reason: 'dismissed' };
      }
    } catch (error) {
      console.error('[PWA] Erreur installation:', error);
      return { success: false, reason: 'error', error };
    }
  }, []);

  // ============================================
  // SERVICE WORKER
  // ============================================

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      console.warn('[PWA] Service Worker non supporté');
      return;
    }

    const registerSW = async () => {
      try {
        const reg = await navigator.serviceWorker.register(SW_PATH, {
          scope: '/',
          updateViaCache: 'none'
        });
        
        setRegistration(reg);
        console.log('[PWA] Service Worker enregistré:', reg.scope);

        // Récupérer la version
        getSWVersion();

        // Écouter les mises à jour
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
                console.log('[PWA] Nouvelle version disponible');
              }
            });
          }
        });

        // Vérifier régulièrement les mises à jour
        updateIntervalRef.current = setInterval(() => {
          reg.update().catch(console.error);
        }, SW_CHECK_INTERVAL);

      } catch (error) {
        console.error('[PWA] Erreur enregistrement SW:', error);
      }
    };

    registerSW();

    // Écouter les messages du SW
    navigator.serviceWorker.addEventListener('message', handleSWMessage);

    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
      navigator.serviceWorker.removeEventListener('message', handleSWMessage);
    };
  }, []);

  /**
   * Gérer les messages du Service Worker
   */
  const handleSWMessage = useCallback((event) => {
    const { type, data } = event.data || {};

    switch (type) {
      case 'SW_ACTIVATED':
        console.log('[PWA] SW activé, version:', data?.version);
        setSwVersion(data?.version);
        setUpdateAvailable(false);
        break;
        
      case 'SYNC_COMPLETE':
        console.log('[PWA] Synchronisation terminée:', data?.tag);
        break;
        
      case 'NOTIFICATION_CLICK':
        console.log('[PWA] Clic notification:', data);
        // Émettre un événement personnalisé pour la navigation
        window.dispatchEvent(new CustomEvent('pwa-notification-click', { detail: data }));
        break;
        
      default:
        console.log('[PWA] Message SW non géré:', type);
    }
  }, []);

  /**
   * Récupérer la version du SW
   */
  const getSWVersion = useCallback(async () => {
    if (!navigator.serviceWorker.controller) return null;

    return new Promise((resolve) => {
      const channel = new MessageChannel();
      
      channel.port1.onmessage = (event) => {
        setSwVersion(event.data?.version);
        resolve(event.data?.version);
      };

      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_VERSION' },
        [channel.port2]
      );

      // Timeout après 3s
      setTimeout(() => resolve(null), 3000);
    });
  }, []);

  /**
   * Appliquer la mise à jour du SW
   */
  const applyUpdate = useCallback(async () => {
    if (!registration?.waiting) {
      console.warn('[PWA] Pas de mise à jour en attente');
      return false;
    }

    // Demander au nouveau SW de prendre le contrôle
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });

    // Recharger après l'activation
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    }, { once: true });

    return true;
  }, [registration]);

  /**
   * Vider le cache
   */
  const clearCache = useCallback(async () => {
    if (!navigator.serviceWorker.controller) {
      console.warn('[PWA] Pas de SW actif');
      return false;
    }

    return new Promise((resolve) => {
      const channel = new MessageChannel();
      
      channel.port1.onmessage = (event) => {
        resolve(event.data?.success || false);
      };

      navigator.serviceWorker.controller.postMessage(
        { type: 'CLEAR_CACHE' },
        [channel.port2]
      );

      setTimeout(() => resolve(false), 5000);
    });
  }, []);

  /**
   * Forcer la mise à jour du SW
   */
  const checkForUpdates = useCallback(async () => {
    if (!registration) return false;
    
    try {
      await registration.update();
      return true;
    } catch (error) {
      console.error('[PWA] Erreur vérification màj:', error);
      return false;
    }
  }, [registration]);

  return {
    // États
    isInstalled,
    isInstallable,
    isOnline,
    isStandalone,
    updateAvailable,
    swVersion,
    registration,
    
    // Actions
    installPWA,
    applyUpdate,
    clearCache,
    checkForUpdates,
    
    // Détection plateforme
    isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream,
    isAndroid: /Android/.test(navigator.userAgent),
    isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
    
    // Support features
    supportsPush: 'PushManager' in window,
    supportsSync: 'SyncManager' in window,
    supportsPeriodicSync: 'PeriodicSyncManager' in window
  };
}

export default usePWA;
